import {
  BasePaginateParamsSchema,
  BasePaginateResultSchema,
} from '@/api/common.ts'
import { z } from 'zod'

// Reuse schemas from sale1010
export const ReservationStatusSchema = z.union([
  z.literal(0), // NotReserved
  z.literal(1), // ReservationInProgress
  z.literal(2), // FullyReserved
  z.literal(3), // ReservationFailed
])

export const ReservationStatusParamSchema = z.coerce
  .number()
  .pipe(ReservationStatusSchema)

export const ReservationFailureReasonSchema = z
  .union([
    z.literal(0), // InsufficientInventory
    z.literal(1), // SystemError
    z.literal(2), // ValidationError
    z.literal(3), // InventoryLocked
    z.literal(4), // MaxRetryExceeded
  ])
  .nullable()

export const ReservedLotSchema = z.object({
  lotNo: z.string(),
  lotDate: z.coerce.date(),
  qty: z.number(),
  reservedAt: z.coerce.date(),
})

// Dashboard Summary Schemas
export const Sale1030DashboardSummarySchema = z.object({
  totalOrdersPendingReservation: z.number(),
  ordersWithUpcomingDelivery: z.number(),
  failedReservationsRequiringAttention: z.number(),
  ordersInPickingProcess: z.number(),
  ordersInLoadingProcess: z.number(),
  reservationStatusCounts: z.array(
    z.object({
      status: ReservationStatusSchema,
      count: z.number(),
    })
  ),
  upcomingDeliveries: z.array(
    z.object({
      id: z.number(),
      number: z.string(),
      deliveryDate: z.coerce.date(),
      deliveryShift: z.string().nullable(),
      customer: z.object({
        id: z.number(),
        code: z.string(),
        name: z.string(),
      }),
      reservationStatus: ReservationStatusSchema,
    })
  ),
})

export type Sale1030DashboardSummary = z.infer<
  typeof Sale1030DashboardSummarySchema
>

// Status List Search Parameters
export const Sale1030StatusSearchParamsSchema = z
  .object({
    deliveryDateFrom: z.coerce.date().optional(),
    deliveryDateTo: z.coerce.date().optional(),
    reservationStatus: ReservationStatusParamSchema.optional(),
    customerId: z.coerce.number().optional(),
    customerCode: z.string().optional(),
    orderNumber: z.string().optional(),
    hasFailures: z.coerce.boolean().optional(),
  })
  .merge(BasePaginateParamsSchema)

export type Sale1030StatusSearchParams = z.infer<
  typeof Sale1030StatusSearchParamsSchema
>

// Customer Schema
export const CustomerSchema = z.object({
  id: z.number(),
  code: z.string(),
  name: z.string(),
})

// Delivery Order Item Status Schema
export const SaleOrderItemStatusSchema = z.object({
  id: z.number(),
  item: z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
    unit: z.string(),
  }),
  orderedQty: z.number(),
  reservedQty: z.number(),
  pickedQty: z.number(),
  deliveredQty: z.number(),
  isFullyReserved: z.boolean(),
  reservedLots: z.array(ReservedLotSchema),
})

export type SaleOrderItemStatus = z.infer<typeof SaleOrderItemStatusSchema>

// Delivery Order Status Schema
export const SaleOrderStatusSchema = z.object({
  id: z.number(),
  number: z.string(),
  date: z.coerce.date(),
  deliveryDate: z.coerce.date(),
  deliveryShift: z.string().nullable(),
  customer: CustomerSchema,
  refNo: z.string().nullable(),
  refDate: z.coerce.date().nullable(),
  remark: z.string().nullable(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  reservationStatus: ReservationStatusSchema,
  reservationFailureCount: z.number(),
  lastReservationAttemptAt: z.coerce.date().nullable(),
  reservedAt: z.coerce.date().nullable(),
  reservationFailureReason: ReservationFailureReasonSchema,
  reservationFailureMessage: z.string().nullable(),
  relatedInv2210DocumentId: z.number().nullable(),
  items: z.array(SaleOrderItemStatusSchema),
})

export type SaleOrderStatus = z.infer<typeof SaleOrderStatusSchema>

// Status List Response Schema
export const Sale1030StatusListResponseSchema = z
  .object({
    data: z.array(SaleOrderStatusSchema),
  })
  .merge(BasePaginateResultSchema)

export type Sale1030StatusListResponse = z.infer<
  typeof Sale1030StatusListResponseSchema
>

// Reservation History Schema
export const ReservationAttemptSchema = z.object({
  attemptAt: z.coerce.date(),
  resultStatus: ReservationStatusSchema,
  failureReason: ReservationFailureReasonSchema,
  failureMessage: z.string().nullable(),
  relatedInv2210DocumentId: z.number().nullable(),
})

export const Sale1030ReservationHistorySchema = z.object({
  saleOrderId: z.number(),
  saleOrderNumber: z.string(),
  reservationAttempts: z.array(ReservationAttemptSchema),
})

export type Sale1030ReservationHistory = z.infer<
  typeof Sale1030ReservationHistorySchema
>
